// ---------------------------------------------------------------------------
// HourlyAuthService — handles login/token management for managers via Hourly
// ---------------------------------------------------------------------------

import { hourlyAuthClient } from "../lib/hourly-orpc-client";

const STORAGE_KEY_HOURLY_TOKEN = "ar_hourly_token";
const STORAGE_KEY_HOURLY_REFRESH_TOKEN = "ar_hourly_refresh_token";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface JwtPayload {
  exp?: number;
  userId?: string;
  companyId?: string;
  name?: string;
  sub?: string;
}

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

function getTokenExpirySeconds(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return payload?.exp ?? null;
}

/**
 * Returns true if the token is expired or within `bufferSeconds` of expiry.
 */
export function isTokenNearExpiry(token: string, bufferSeconds = 300): boolean {
  const exp = getTokenExpirySeconds(token);
  if (exp == null) return false;
  return Date.now() / 1000 > exp - bufferSeconds;
}

// ---------------------------------------------------------------------------
// Auto-refresh scheduling
// ---------------------------------------------------------------------------

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;
let _onTokenRefreshed: ((tokens: { accessToken: string; refreshToken: string }) => void) | null = null;
let _onRefreshFailed: (() => void) | null = null;

function clearRefreshTimer(): void {
  if (_refreshTimer !== null) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

/**
 * Schedule a silent token refresh 5 minutes before the access token expires.
 * Call this after login and after each successful refresh.
 */
function scheduleTokenRefresh(token: string): void {
  clearRefreshTimer();
  const exp = getTokenExpirySeconds(token);
  if (exp == null) return;

  // Refresh 5 minutes before expiry
  const msUntilRefresh = (exp - 300) * 1000 - Date.now();
  if (msUntilRefresh <= 0) {
    // Already near expiry — refresh immediately in background
    void doSilentRefresh();
    return;
  }

  _refreshTimer = setTimeout(() => {
    void doSilentRefresh();
  }, msUntilRefresh);
}

async function doSilentRefresh(): Promise<void> {
  const refreshToken = localStorage.getItem(STORAGE_KEY_HOURLY_REFRESH_TOKEN);
  if (!refreshToken) {
    _onRefreshFailed?.();
    return;
  }

  try {
    const result = await HourlyAuthService.refreshToken(refreshToken);
    localStorage.setItem(STORAGE_KEY_HOURLY_TOKEN, result.accessToken);
    if (result.refreshToken) {
      localStorage.setItem(STORAGE_KEY_HOURLY_REFRESH_TOKEN, result.refreshToken);
    }
    _onTokenRefreshed?.(result);
    scheduleTokenRefresh(result.accessToken);
  } catch {
    _onRefreshFailed?.();
  }
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const HourlyAuthService = {
  /**
   * POST /orpc/auth/login — authenticate with Hourly credentials.
   * Stores tokens in localStorage and schedules auto-refresh.
   */
  async login(email: string, password: string) {
    try {
      const data = await hourlyAuthClient.login({ email, password });

      // Persist tokens
      localStorage.setItem(STORAGE_KEY_HOURLY_TOKEN, data.tokens.accessToken);
      if (data.tokens.refreshToken) {
        localStorage.setItem(STORAGE_KEY_HOURLY_REFRESH_TOKEN, data.tokens.refreshToken);
      }

      // Schedule auto-refresh
      scheduleTokenRefresh(data.tokens.accessToken);

      return data;
    } catch (err) {
      // oRPC errors include status in the cause chain
      if (err instanceof Error && "status" in err) {
        const status = (err as { status: number }).status;
        if (status === 401 || status === 403) {
          throw new Error("Invalid email or password");
        }
      }
      throw err instanceof Error ? err : new Error("Login failed");
    }
  },

  /**
   * POST /orpc/auth/refresh — exchange a refresh token for new tokens.
   */
  async refreshToken(refreshToken: string) {
    try {
      const data = await hourlyAuthClient.refresh({ refreshToken });
      return data;
    } catch {
      throw new Error("Token refresh failed");
    }
  },

  /**
   * Check if the stored access token is still valid (not expired).
   */
  validateSession(): boolean {
    const token = localStorage.getItem(STORAGE_KEY_HOURLY_TOKEN);
    if (!token) return false;
    return !isTokenNearExpiry(token, 0);
  },

  /**
   * Schedule a silent token refresh 5 minutes before the access token expires.
   * Exposed so AuthContext can call it when restoring a persisted session.
   */
  scheduleTokenRefresh(token: string): void {
    scheduleTokenRefresh(token);
  },

  /**
   * Register callbacks for token lifecycle events.
   * Call from AuthProvider to keep auth state in sync with auto-refresh.
   */
  onTokenRefreshed(cb: (tokens: { accessToken: string; refreshToken: string }) => void): void {
    _onTokenRefreshed = cb;
  },

  onRefreshFailed(cb: () => void): void {
    _onRefreshFailed = cb;
  },

  /**
   * Cancel scheduled refresh and clear callbacks (call on logout).
   */
  teardown(): void {
    clearRefreshTimer();
    _onTokenRefreshed = null;
    _onRefreshFailed = null;
  },
};;
